import { ASSET_GROUPS, COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { CURRENCY } from '@/utils/currency';
import { PortfolioData, HOLDING_TYPES } from '@/utils/portfolios';
import type ComparisonHolding from '@/types/comparison-holding';
import type PortfolioHolding from '@/types/portfolio-holding';

export const convertHoldingsToComparisonHoldings = (
  holdings: PortfolioHolding[],
  data?: PortfolioData,
  existingHoldings?: ComparisonHolding[],
): ComparisonHolding[] => {
  const HOLDING_TYPE_CONVERTERS = {
    [HOLDING_TYPES.FUND]: convertFundHolding,
    [HOLDING_TYPES.CUSTOM_YIELD]: convertFundHolding,
    [COMPARISON_ITEM_TYPES.STOCK]: convertStockHolding,
    [HOLDING_TYPES.ALT]: convertAltHolding,
  } as const;

  return holdings
    .filter(h => !!h.original)
    .map(h => {
      const comparisonHolding = createBaseComparisonHolding(h);
      const converter = HOLDING_TYPE_CONVERTERS[h.original.type as keyof typeof HOLDING_TYPE_CONVERTERS];
      const convertedHolding = converter ? converter(h, comparisonHolding, data) : comparisonHolding;

      // Preserve tax-metrics from read only comparison existing holdings if available
      const existingHolding = existingHoldings?.find(eh => eh.id === h.original.id);
      if (existingHolding) {
        if (existingHolding.taxMetrics) {
          convertedHolding.taxMetrics = existingHolding.taxMetrics;
        }
      }

      return convertedHolding;
    });
};

const createBaseComparisonHolding = (holding: PortfolioHolding): ComparisonHolding =>
  ({
    id: holding.original.id,
    type: holding.original.type,
    yieldParams: holding.original?.yieldParams,
    name: holding.name,
    symbol: holding.symbol,
    weight: holding.weight,
    units: holding.units,
    currency: holding.currency,
    filteredShelves: holding?.filteredShelves || [],
    complianceComparisons: holding?.complianceComparisons,
    ...(!!holding.taxSettings && { taxSettings: holding.taxSettings }),
    ...(!!holding.lots && { lots: holding.lots }),
  }) as ComparisonHolding;

const convertFundHolding = (holding: PortfolioHolding, comparisonHolding: ComparisonHolding, data?: PortfolioData): ComparisonHolding => {
  const fund = data?.funds.find(f => f._id === holding.original.id);

  if (!fund?.performance) return comparisonHolding;

  const {
    assetGroup,
    assetType,
    classification,
    currency,
    customData,
    distributionYield,
    dates,
    distribution,
    mer,
    nav,
    riskRating,
    totalYield,
    performance,
    symbols,
    universe,
  } = fund;

  return {
    ...comparisonHolding,
    symbols,
    universe,
    rolling: performance.rolling,
    baseRolling: currency === CURRENCY.USD ? performance.rollingUSD : performance.rolling,
    nav,
    distribution,
    assetGroup,
    assetType,
    classification,
    dates,
    riskRating,
    metrics: {
      percentGrowth: performance.percentGrowth,
      basePercentGrowth: currency === CURRENCY.USD ? performance.percentGrowthUSD : undefined,
      mer,
      distributionYield,
      totalYield,
      ...(fund.fundPortfolio?.characteristics?.yieldToMaturity != null ? { yieldToMaturity: fund.fundPortfolio.characteristics.yieldToMaturity } : {}),
    },
    customData,
  };
};

const convertStockHolding = (holding: PortfolioHolding, comparisonHolding: ComparisonHolding, data?: PortfolioData): ComparisonHolding => {
  const stock = data?.stocks.find(f => f._id === holding.original.id);

  if (!stock?.performance) return comparisonHolding;

  const { assetGroup, classification, distribution, currency, dates, nav, performance } = stock;

  return {
    ...comparisonHolding,
    rolling: performance.rolling,
    baseRolling: currency === CURRENCY.USD ? performance.rollingUSD : performance.rolling,
    nav,
    distribution,
    assetGroup,
    classification,
    dates,
    metrics: {
      percentGrowth: performance.percentGrowth,
      basePercentGrowth: currency === CURRENCY.USD ? performance.percentGrowthUSD : undefined,
    },
  };
};

const convertAltHolding = (holding: PortfolioHolding, comparisonHolding: ComparisonHolding, data?: PortfolioData): ComparisonHolding => {
  const alt = data?.alts.find(f => f._id === holding.original.id);

  if (!alt?.performance) return comparisonHolding;

  const { classification, currency, dates, performance } = alt;

  return {
    ...comparisonHolding,
    rolling: performance.rolling,
    assetGroup: ASSET_GROUPS.ALTERNATIVE_INVESTMENT,
    baseRolling: currency === CURRENCY.USD ? performance.rollingUSD : performance.rolling,
    classification,
    dates,
    metrics: {
      percentGrowth: performance.percentGrowth,
      basePercentGrowth: currency === CURRENCY.USD ? performance.percentGrowthUSD : undefined,
    },
    hasPerformanceFromBenchmark: alt.hasPerformanceFromBenchmark,
  };
};
